import { Outlet } from "react-router-dom";

import { useCan } from "../../auth/permissions.js";
import { SectionHeader, type SectionTab } from "../../components/SectionHeader.js";

/**
 * System Admin shell — six tabs under one shared header + tab strip:
 *
 *   - User Management     (`/admin/users`)              — dashboard accounts
 *                          + the editable role/permission matrix.
 *   (Reseller Accounts — the PEOPLE inside reseller companies — and Reseller
 *   Applications — companies asking to become one — were tabs here until
 *   2026-09-12. Both live in the CRM now: Accounts is a tab of the reseller
 *   profile, Applications a tab of Reseller Management behind its own
 *   `crm:reseller_applications` grant. `/admin/resellers` and
 *   `/admin/reseller-applications*` redirect there.)
 *   (Products & Releases and Price List were tabs here until 2026-09-12.
 *   They live in the CRM's Products section now — CRM ▸ Releases
 *   (`/crm/releases`), Licences (`/crm/licences`) and Pricing
 *   (`/crm/pricing`). `/admin/products-releases` redirects to
 *   `/crm/releases` and `/admin/price-list` to `/crm/pricing`.)
 *   - Otto Bots           (`/admin/otto`)               — named Otto
 *                          deployments: embed keys, knowledge scope, host
 *                          allowlist (managing bots is system admin).
 *   - AI Providers        (`/admin/ai-providers`)       — provider selection.
 *   - System Emails       (`/admin/system-emails`)      — which admins and
 *                          salespeople receive each system email (licence
 *                          approvals, reseller quotes, reseller applications).
 *   - Security            (`/admin/security`)           — the security monitor.
 *   - Change Log          (`/admin/changelog`)          — the cross-repo log.
 *
 * Title + tab chrome come from the shared `<SectionHeader>` — same chrome as
 * the Support / Marketing / Sales shells. `/admin` and every legacy
 * `/admin/*` URL redirect to `/admin/users` (wired in App.tsx) so external
 * bookmarks survive.
 */
/** Tabs behind the `admin:users` gate — the dashboard-identity surfaces. */
const ADMIN_TABS: SectionTab[] = [
  { to: "users",             label: "User Management" },
  { to: "otto",              label: "Otto Bots" },
  { to: "ai-providers",      label: "AI Providers" },
  { to: "system-emails",     label: "System Emails" },
  { to: "security",          label: "Security" },
  { to: "changelog",         label: "Change Log" },
];

export function AdminLayout(): JSX.Element {
  // The tab strip is FILTERED, not just route-guarded. Rendering a tab that
  // bounces to Forbidden is worse than not rendering it: it advertises a
  // surface the operator cannot use and reads as a bug rather than a boundary.
  const canAdminUsers = useCan("admin:users");

  const tabs: SectionTab[] = canAdminUsers ? ADMIN_TABS : [];

  return (
    <>
      <SectionHeader
        title="System Admin"
        subtitle="Dashboard accounts & roles, Otto bots, AI providers, system email recipients, the security monitor and the change log."
        tabs={tabs}
        tabsAriaLabel="System Admin sections"
      />
      <Outlet />
    </>
  );
}
